import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  MarketplaceStatus,
  PopulatedCollection,
} from "../../../api/collection";
import shopifyProductPageApiClient from "../../../api/shopifyProductPage";

/**
 * Predicts whether the product-page preview request can succeed, so callers can
 * disable the button with a reason instead of firing a request that 400s.
 *
 * Checks only what the api actually needs: a named creator (it rejects an empty
 * one outright) and klydos with animations (the product media is built from
 * `loopUrl`). Neither review axis gates this — a collection may hold klydos that
 * were never submitted for public review, and the reviewer previews a
 * collection precisely while it is still pending.
 */
export const getPreviewReadiness = (
  collection?: Partial<PopulatedCollection>,
): { isReady: boolean; reason: string } => {
  if (!collection?.creator?.name?.trim()) {
    return {
      isReady: false,
      reason: "The collection's creator has no name set.",
    };
  }
  const klydos = collection.klydos ?? [];
  if (!klydos.length || klydos.some((klydo) => !klydo.loopUrl)) {
    return {
      isReady: false,
      reason:
        "The collection must have klydos with uploaded animations before generating a preview.",
    };
  }
  return { isReady: true, reason: "" };
};

export const useShopifyPreviewUrlQuery = (
  collectionId: string | undefined,
  collection?: Partial<PopulatedCollection>,
) => {
  const cacheKey = useMemo(() => {
    if (!collection) return null;

    const relevantFields = {
      name: collection.name,
      coverUrl: collection.coverUrl,
      logoUrl: collection.logoUrl,
      klydoIds: collection.klydoIds,
      description: collection.description,
      logoTypeUrl: collection.productPageMetadata?.logoTypeUrl,
      thumbnailBackgroundUrl:
        collection.productPageMetadata?.thumbnailBackgroundUrl,
      badgeColorHex: collection.productPageMetadata?.badgeColorHex,
      labels: collection.productPageMetadata?.labels,
    };

    return JSON.stringify(relevantFields);
  }, [
    collection?.name,
    collection?.coverUrl,
    collection?.logoUrl,
    collection?.klydoIds,
    collection?.description,
    collection?.productPageMetadata?.logoTypeUrl,
    collection?.productPageMetadata?.thumbnailBackgroundUrl,
    collection?.productPageMetadata?.badgeColorHex,
    collection?.productPageMetadata?.labels,
  ]);

  const isReadyForPreview = getPreviewReadiness(collection).isReady;

  return useQuery({
    queryKey: ["collection-shopify-preview-url", collectionId, cacheKey],
    queryFn: () =>
      shopifyProductPageApiClient.getCollectionShopifyPreviewUrl(collectionId!),
    enabled:
      !!collectionId &&
      !!collection &&
      collection?.marketplace?.status !== MarketplaceStatus.Pending &&
      isReadyForPreview,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
};
